import type PptxGenJS from 'pptxgenjs';

import clientLogos from '../../../../../../../images/client-logos.svg';
import { loadImage } from './ppt-chart-image';
import { addReportFooterSlide, FOOTER_TEXT } from './ppt-slide-generator';
import {
  getActiveBrandConfig,
  pptTheme,
  REPORT_WIDE_COVER_MASTER,
  REPORT_WIDE_DARK_MASTER,
  reportWidePptTheme,
} from './ppt-theme';

const BACKGROUND_W_PX = 1920;
const BACKGROUND_H_PX = 1080;
const GRADIENT_BOTTOM_ALPHA = 0.45;
const LOGO_ASPECT_RATIO = 185 / 27;
const CLIENT_LOGOS_ASPECT = 232 / 257;
const LOGO_CAPTURE_SCALE = 4;
const MARGIN_X = 0.6;
const MARGIN_TOP = 0.5;
const MARGIN_BOTTOM = 0.6;
const TITLE_TOP_IN = 1.2;
const TITLE_H_IN = 1.3;
const COVER_GAP_IN = 0.3;
const PARAM_ROW_IN = 0.6;
const PARAM_ROW_MIN_IN = 0.42;
const PARAM_COLUMN_GAP_IN = 0.3;
const DESCRIPTION_MIN_H_IN = 0.6;
const DESCRIPTION_MAX_PT = 13;
const DESCRIPTION_MIN_PT = 9;
const DESCRIPTION_LINE_HEIGHT = 1.25;
const AVG_CHAR_WIDTH_EM = 0.52;

const hex = (color: string) => color.replace('#', '');

const descriptionFontPt = (text: string, widthIn: number, heightIn: number) => {
  for (let pt = DESCRIPTION_MAX_PT; pt > DESCRIPTION_MIN_PT; pt -= 1) {
    const charsPerLine = Math.max(
      8,
      Math.floor((widthIn * 72) / (pt * AVG_CHAR_WIDTH_EM))
    );
    const lines = text
      .split('\n')
      .reduce(
        (sum, line) => sum + Math.max(1, Math.ceil(line.length / charsPerLine)),
        0
      );
    if ((lines * pt * DESCRIPTION_LINE_HEIGHT) / 72 <= heightIn) return pt;
  }
  return DESCRIPTION_MIN_PT;
};

const darkPageColor = () =>
  getActiveBrandConfig().coverBackgroundColor ??
  reportWidePptTheme.colors.darkSurface;

export const darkPageBackground = (): PptxGenJS.BackgroundProps => {
  const color = darkPageColor();
  try {
    const canvas = document.createElement('canvas');
    canvas.width = BACKGROUND_W_PX;
    canvas.height = BACKGROUND_H_PX;
    const context = canvas.getContext('2d');
    if (!context) return { color: hex(color) };
    context.fillStyle = color;
    context.fillRect(0, 0, BACKGROUND_W_PX, BACKGROUND_H_PX);
    const gradient = context.createLinearGradient(0, 0, 0, BACKGROUND_H_PX);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(1, `rgba(0, 0, 0, ${GRADIENT_BOTTOM_ALPHA})`);
    context.fillStyle = gradient;
    context.fillRect(0, 0, BACKGROUND_W_PX, BACKGROUND_H_PX);
    return { data: canvas.toDataURL('image/png') };
  } catch {
    return { color: hex(color) };
  }
};

export const addWideCoverSlide = (
  pptx: PptxGenJS,
  {
    title,
    description,
    parameters,
  }: {
    title: string;
    description?: string;
    parameters: { label: string; value: string }[];
  }
) => {
  const { widthInches, heightInches } = reportWidePptTheme.slide;
  const brand = getActiveBrandConfig();
  const fontFace = pptTheme.fonts.face;
  const titleColor = hex(brand.coverTitleColor);
  const textColor = hex(brand.coverDescriptionColor);
  const columnW = widthInches * 0.55;
  const slide = pptx.addSlide({ masterName: REPORT_WIDE_COVER_MASTER });

  const logoH = 0.26;
  slide.addImage({
    path: brand.logos.reportLogo,
    x: MARGIN_X,
    y: MARGIN_TOP,
    w: logoH * LOGO_ASPECT_RATIO,
    h: logoH,
  });

  const descriptionTop = TITLE_TOP_IN + TITLE_H_IN + COVER_GAP_IN;
  const paramsCeiling =
    descriptionTop +
    (description?.trim() ? DESCRIPTION_MIN_H_IN + COVER_GAP_IN : 0);
  const paramsAvailableH = heightInches - MARGIN_BOTTOM - paramsCeiling;
  const columns =
    parameters.length * PARAM_ROW_MIN_IN > paramsAvailableH ? 2 : 1;
  const rowsPerColumn = Math.max(1, Math.ceil(parameters.length / columns));
  const rowIn = Math.min(PARAM_ROW_IN, paramsAvailableH / rowsPerColumn);
  const paramsH = rowsPerColumn * rowIn;
  const paramsTop = heightInches - MARGIN_BOTTOM - paramsH;
  const descriptionH = paramsTop - COVER_GAP_IN - descriptionTop;

  slide.addText(title || 'Report', {
    x: MARGIN_X,
    y: TITLE_TOP_IN,
    w: columnW,
    h: TITLE_H_IN,
    fontFace,
    fontSize: 40,
    color: titleColor,
    valign: 'bottom',
    margin: 0,
    lineSpacingMultiple: 0.95,
    fit: 'shrink',
  });

  if (description?.trim() && descriptionH >= COVER_GAP_IN) {
    slide.addText(description, {
      x: MARGIN_X,
      y: descriptionTop,
      w: columnW,
      h: descriptionH,
      fontFace,
      fontSize: descriptionFontPt(description, columnW, descriptionH),
      color: textColor,
      valign: 'top',
      margin: 0,
      lineSpacingMultiple: 1.25,
      fit: 'shrink',
    });
  }

  if (parameters.length === 0) return;
  const valueSpaceAfter = Math.round((14 * rowIn) / PARAM_ROW_IN);
  const paramColumnW =
    (columnW - PARAM_COLUMN_GAP_IN * (columns - 1)) / columns;
  for (let column = 0; column < columns; column += 1) {
    const slice = parameters.slice(
      column * rowsPerColumn,
      (column + 1) * rowsPerColumn
    );
    if (slice.length === 0) continue;
    const runs = slice.flatMap(
      ({ label, value }, index): PptxGenJS.TextProps[] => [
        {
          text: label,
          options: {
            fontSize: 10,
            color: textColor,
            transparency: 30,
            breakLine: true,
            paraSpaceAfter: 4,
          },
        },
        {
          text: value,
          options: {
            fontSize: 12,
            color: titleColor,
            breakLine: index < slice.length - 1,
            paraSpaceAfter: valueSpaceAfter,
          },
        },
      ]
    );
    slide.addText(runs, {
      x: MARGIN_X + column * (paramColumnW + PARAM_COLUMN_GAP_IN),
      y: paramsTop,
      w: paramColumnW,
      h: paramsH,
      fontFace,
      valign: 'bottom',
      margin: 0,
      fit: 'shrink',
    });
  }
};

const recoloredClientLogos = async (
  color: string,
  widthIn: number,
  heightIn: number
): Promise<string | null> => {
  try {
    const markup = await fetch(clientLogos).then((response) => response.text());
    const recolored = markup.replace(
      /fill="#[0-9a-fA-F]{6}"/g,
      `fill="${color}"`
    );
    const image = await loadImage(
      `data:image/svg+xml;charset=utf-8,${encodeURIComponent(recolored)}`
    );
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(widthIn * 96 * LOGO_CAPTURE_SCALE);
    canvas.height = Math.round(heightIn * 96 * LOGO_CAPTURE_SCALE);
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
};

export const addWideClosingSlide = async (pptx: PptxGenJS) => {
  const brand = getActiveBrandConfig();
  if (brand.closingSlide) {
    addReportFooterSlide(pptx, reportWidePptTheme.slide);
    return;
  }

  const { widthInches } = reportWidePptTheme.slide;
  const fontFace = pptTheme.fonts.face;
  const titleColor = hex(brand.coverTitleColor);
  const textColor = hex(brand.coverDescriptionColor);
  const contentW = widthInches - 2 * MARGIN_X;
  const gap = 0.6;
  const leftW = contentW * 0.62;
  const rightX = MARGIN_X + leftW + gap;
  const rightW = contentW - leftW - gap;
  const slide = pptx.addSlide({ masterName: REPORT_WIDE_DARK_MASTER });

  const heading = (text: string, fontSize = 18): PptxGenJS.TextProps => ({
    text,
    options: {
      fontSize,
      color: titleColor,
      breakLine: true,
      paraSpaceAfter: 8,
    },
  });
  const body = (text: string, breakLine = false): PptxGenJS.TextProps => ({
    text,
    options: { fontSize: 11, color: textColor, breakLine },
  });

  slide.addText(
    [heading(FOOTER_TEXT.aboutTitle), body(FOOTER_TEXT.aboutBody)],
    {
      x: MARGIN_X,
      y: 0.8,
      w: leftW,
      h: 2.6,
      fontFace,
      valign: 'top',
      margin: 0,
      lineSpacingMultiple: 1.2,
      fit: 'shrink',
    }
  );

  const logosH = 2.6;
  const logosW = Math.min(rightW, logosH * CLIENT_LOGOS_ASPECT);
  const logos = await recoloredClientLogos(
    brand.coverDescriptionColor,
    logosW,
    logosH
  );
  if (logos) {
    slide.addImage({
      data: logos,
      x: rightX + (rightW - logosW) / 2,
      y: 0.8,
      w: logosW,
      h: logosH,
    });
  }

  slide.addText(
    [heading(FOOTER_TEXT.contactTitle), body(FOOTER_TEXT.contactBody)],
    {
      x: MARGIN_X,
      y: 3.9,
      w: leftW,
      h: 2.9,
      fontFace,
      valign: 'top',
      margin: 0,
      lineSpacingMultiple: 1.2,
      fit: 'shrink',
    }
  );

  slide.addText(
    [
      heading(FOOTER_TEXT.getInTouchTitle, 14),
      body(FOOTER_TEXT.getInTouchBody, true),
      { text: ' ', options: { fontSize: 6, breakLine: true } },
      {
        text: `${FOOTER_TEXT.emailTitle}: `,
        options: { fontSize: 11, bold: true, color: titleColor },
      },
      body(FOOTER_TEXT.emailBody, true),
      {
        text: `${FOOTER_TEXT.visitTitle}: `,
        options: { fontSize: 11, bold: true, color: titleColor },
      },
      body(FOOTER_TEXT.visitBody),
    ],
    {
      x: rightX,
      y: 3.9,
      w: rightW,
      h: 2.9,
      fontFace,
      valign: 'top',
      margin: 0,
      lineSpacingMultiple: 1.2,
      fit: 'shrink',
    }
  );
};
